(() => {
  'use strict';

  const FORM_HOST_SELECTOR = 'shopify-forms-embed#app-embed, shopify-forms-embed';
  const SPACE_BUTTON_SELECTOR =
    'button[aria-labelledby^="custom#space_interested_in-selector-label"]';
  const SPACE_OPTION_SELECTOR =
    '[role="option"][id^="select-custom#space_interested_in-"]';
  const TIMING_INPUT_SELECTOR = 'input[id="custom#preferred_move-in_timing"]';
  const COUNTRY_BUTTON_SELECTOR = 'button[aria-label^="Country code"]';
  const STYLE_ID = 'yelo-form-enhancement-styles';
  const ROOT_OBSERVER_KEY = 'yeloEnhancementObserver';

  const getConfiguration = () => {
    const wrapper = document.querySelector('[data-yelo-form-enhancements]');
    if (!wrapper) return null;

    let timingOptions = [];
    let spaceOptions = [];
    try {
      timingOptions = JSON.parse(wrapper.dataset.timingOptions || '[]').filter(Boolean);
    } catch (error) {
      timingOptions = [];
    }
    try {
      spaceOptions = JSON.parse(wrapper.dataset.spaceOptions || '[]').filter(Boolean);
    } catch (error) {
      spaceOptions = [];
    }

    return {
      wrapper,
      countryCode: (wrapper.dataset.defaultCountry || 'GB').trim().toUpperCase(),
      spaceLabel: wrapper.dataset.spaceLabel || 'Space interested in',
      spaceOptions: spaceOptions.length
        ? spaceOptions
        : ['Compact', 'Standard', 'Large', 'Not sure yet'],
      timingLabel: wrapper.dataset.timingLabel || 'Preferred move-in timing',
      timingOptions: timingOptions.length
        ? timingOptions
        : ['Now', '1 week', '1 month', '3 months', '6 months'],
    };
  };

  const setReactInputValue = (input, value) => {
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;

    if (setter) {
      setter.call(input, value);
    } else {
      input.value = value;
    }

    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  };

  const installStyles = (root) => {
    if (root.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .yelo-original-timing-field {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
        pointer-events: none !important;
      }

      .yelo-original-timing-label,
      .yelo-original-timing-field + label {
        display: none !important;
      }

      [data-yelo-space-original="true"] {
        display: none !important;
      }

      .yelo-space-select-container {
        position: relative;
        width: 100%;
      }

      .yelo-space-select-label {
        margin: 0;
        color: #2a2b2d;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
      }

      .yelo-form-native-select {
        appearance: none;
        -webkit-appearance: none;
        box-sizing: border-box;
        display: block;
        width: 100%;
        min-height: 47.5px;
        margin: 0;
        padding: 0 44px 0 15px;
        border: 0;
        border-radius: 5px;
        outline: 0;
        box-shadow: rgba(32, 32, 32, 0.2) 0 0 0 1px;
        background-color: #fff;
        background-image:
          linear-gradient(45deg, transparent 50%, #666 50%),
          linear-gradient(135deg, #666 50%, transparent 50%);
        background-position:
          calc(100% - 19px) 50%,
          calc(100% - 14px) 50%;
        background-size: 5px 5px, 5px 5px;
        background-repeat: no-repeat;
        color: #202020;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.25;
        cursor: pointer;
      }

      .yelo-form-native-select:focus-visible {
        box-shadow: #2a2b2d 0 0 0 2px;
      }

      .yelo-form-native-select:invalid,
      .yelo-form-native-select option[disabled] {
        color: rgba(32, 32, 32, 0.58);
      }
    `;
    root.appendChild(style);
  };

  const setDefaultCountry = (host, root, countryCode) => {
    if (
      host.dataset.yeloCountryInitialized === 'true' ||
      host.dataset.yeloCountryInitialized === 'pending'
    ) {
      return;
    }

    const countryButton = root.querySelector(COUNTRY_BUTTON_SELECTOR);
    if (!countryButton) return;

    const selectedCountry = countryButton.getAttribute('aria-label') || '';
    if (selectedCountry.includes(`+44`) && countryCode === 'GB') {
      host.dataset.yeloCountryInitialized = 'true';
      return;
    }

    host.dataset.yeloCountryInitialized = 'pending';
    countryButton.click();

    let attempts = 0;
    const selectCountry = () => {
      const option = root.querySelector(
        `[id="select-country-${CSS.escape(countryCode)}"][role="option"]`
      );

      if (option) {
        option.click();
        host.dataset.yeloCountryInitialized = 'true';
        return;
      }

      attempts += 1;
      if (attempts < 30) {
        window.setTimeout(selectCountry, 50);
      } else {
        delete host.dataset.yeloCountryInitialized;
      }
    };

    selectCountry();
  };

  const selectOriginalSpaceOption = (root, selectedLabel) => {
    const originalButton = root.querySelector(SPACE_BUTTON_SELECTOR);
    if (!originalButton) return;

    originalButton.click();

    let attempts = 0;
    const chooseOption = () => {
      const normalizedLabel = selectedLabel.trim().toLowerCase();
      const options = Array.from(root.querySelectorAll(SPACE_OPTION_SELECTOR));
      const matchingOption = options.find((option) =>
        (option.textContent || '').trim().toLowerCase().startsWith(normalizedLabel)
      );

      if (matchingOption) {
        matchingOption.click();
        return;
      }

      attempts += 1;
      if (attempts < 30) window.setTimeout(chooseOption, 50);
    };

    chooseOption();
  };

  const enhanceSpaceField = (root, label, options) => {
    const originalButton = root.querySelector(SPACE_BUTTON_SELECTOR);
    const originalContainer = originalButton?.parentElement;
    if (!originalButton || !originalContainer) return;

    originalContainer.dataset.yeloSpaceOriginal = 'true';
    if (root.querySelector('[data-yelo-space-select="true"]')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'yelo-space-select-container';

    const visibleLabel = document.createElement('p');
    visibleLabel.className = 'yelo-space-select-label';
    visibleLabel.id = 'yelo-space-interested-label';
    visibleLabel.textContent = label;

    const select = document.createElement('select');
    select.className = 'yelo-form-native-select yelo-space-select';
    select.dataset.yeloSpaceSelect = 'true';
    select.setAttribute('aria-labelledby', visibleLabel.id);

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    select.appendChild(placeholder);

    options.forEach((optionLabel) => {
      const option = document.createElement('option');
      option.value = optionLabel;
      option.textContent = optionLabel;
      select.appendChild(option);
    });

    select.addEventListener('change', () => {
      selectOriginalSpaceOption(root, select.value);
    });

    wrapper.append(visibleLabel, select);
    originalContainer.insertAdjacentElement('afterend', wrapper);
  };

  const enhanceTimingField = (root, label, options) => {
    const input = root.querySelector(TIMING_INPUT_SELECTOR);
    if (!input || input.dataset.yeloTimingEnhanced === 'true') return;

    const container = input.parentElement;
    if (!container) return;

    const originalLabel = root.querySelector(
      `label[for="${CSS.escape(input.id)}"]`
    );
    const select = document.createElement('select');
    select.className = 'yelo-form-native-select yelo-move-in-select';
    select.setAttribute('aria-label', label);
    select.dataset.yeloTimingSelect = 'true';
    select.required = input.required;

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = label;
    placeholder.disabled = true;
    placeholder.selected = !input.value;
    select.appendChild(placeholder);

    options.forEach((optionLabel) => {
      const option = document.createElement('option');
      option.value = optionLabel;
      option.textContent = optionLabel;
      option.selected = input.value === optionLabel;
      select.appendChild(option);
    });

    select.addEventListener('change', () => {
      setReactInputValue(input, select.value);
    });

    input.dataset.yeloTimingEnhanced = 'true';
    input.classList.add('yelo-original-timing-field');
    input.tabIndex = -1;

    if (originalLabel) {
      originalLabel.classList.add('yelo-original-timing-label');
    }

    container.appendChild(select);
  };

  const enhance = () => {
    const config = getConfiguration();
    if (!config) return;

    const host = config.wrapper.querySelector(FORM_HOST_SELECTOR);
    const root = host?.shadowRoot;
    if (!host || !root) return;

    installStyles(root);
    setDefaultCountry(host, root, config.countryCode);
    enhanceSpaceField(root, config.spaceLabel, config.spaceOptions);
    enhanceTimingField(root, config.timingLabel, config.timingOptions);

    if (!root[ROOT_OBSERVER_KEY]) {
      const observer = new MutationObserver(() => {
        installStyles(root);
        setDefaultCountry(host, root, config.countryCode);
        enhanceSpaceField(root, config.spaceLabel, config.spaceOptions);
        enhanceTimingField(root, config.timingLabel, config.timingOptions);
      });
      observer.observe(root, { childList: true, subtree: true });
      root[ROOT_OBSERVER_KEY] = observer;
    }
  };

  const documentObserver = new MutationObserver(enhance);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }

  documentObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  document.addEventListener('shopify:section:load', enhance);
})();
